export type ReadingTextBlock = {
  type: "paragraph" | "subheading" | "caption";
  text: string;
};

export type ReadingTableCell = {
  text: string;
  rowSpan: number;
  colSpan: number;
};

export type ReadingTableBlock = {
  type: "table";
  text: string;
  caption: string;
  rows: ReadingTableCell[][];
};

export type ReadingBlock = ReadingTextBlock | ReadingTableBlock;

export type ReadingImage = {
  id: string;
  src: string;
  page: number;
  width: number;
  height: number;
  caption: string;
};

export type ReadingArticle = {
  id: string;
  order: number;
  section: string;
  title: string;
  byline: string | null;
  summary: string;
  tags: string[];
  pageStart: number;
  pageEnd: number;
  blocks: ReadingBlock[];
  source?: {
    section: string;
    title: string;
    summary: string;
    blocks: ReadingBlock[];
  };
  images?: ReadingImage[];
};

export type ReadingDocument = {
  version: number;
  issueId: string;
  issueLabel: string;
  publication?: string;
  language?: "zh" | "en" | "bilingual";
  overview: string;
  sourceOverview?: string;
  translationModel?: string;
  model: string;
  generatedAt: string;
  articles: ReadingArticle[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isBlock(value: unknown): value is ReadingBlock {
  if (!isRecord(value)) return false;
  if (value.type === "table") {
    return (
      typeof value.text === "string"
      && typeof value.caption === "string"
      && Array.isArray(value.rows)
      && value.rows.length > 0
      && value.rows.every(
        (row) => Array.isArray(row) && row.length > 0 && row.every((cell) => (
          isRecord(cell)
          && typeof cell.text === "string"
          && Number.isInteger(cell.rowSpan)
          && Number(cell.rowSpan) >= 1
          && Number(cell.rowSpan) <= 50
          && Number.isInteger(cell.colSpan)
          && Number(cell.colSpan) >= 1
          && Number(cell.colSpan) <= 50
        )),
      )
    );
  }
  return (
    (value.type === "paragraph" || value.type === "subheading" || value.type === "caption")
    && typeof value.text === "string"
  );
}

function isImage(value: unknown): value is ReadingImage {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string"
    && typeof value.src === "string"
    && /^\/api\/issues\/[^/]+\/images\/[a-zA-Z0-9.-]+\.webp$/u.test(value.src)
    && typeof value.page === "number"
    && value.page > 0
    && typeof value.width === "number"
    && value.width > 0
    && typeof value.height === "number"
    && value.height > 0
    && typeof value.caption === "string"
  );
}

function isArticle(value: unknown): value is ReadingArticle {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string"
    && typeof value.order === "number"
    && typeof value.section === "string"
    && typeof value.title === "string"
    && (value.byline === null || typeof value.byline === "string")
    && typeof value.summary === "string"
    && Array.isArray(value.tags)
    && value.tags.every((tag) => typeof tag === "string")
    && typeof value.pageStart === "number"
    && typeof value.pageEnd === "number"
    && Array.isArray(value.blocks)
    && value.blocks.every(isBlock)
    && (
      value.source === undefined
      || (
        isRecord(value.source)
        && typeof value.source.section === "string"
        && typeof value.source.title === "string"
        && typeof value.source.summary === "string"
        && Array.isArray(value.source.blocks)
        && value.source.blocks.every(isBlock)
      )
    )
    && (
      value.images === undefined
      || (Array.isArray(value.images) && value.images.every(isImage))
    )
  );
}

export function isReadingDocument(value: unknown): value is ReadingDocument {
  if (!isRecord(value)) return false;
  return (
    value.version === 1
    && typeof value.issueId === "string"
    && typeof value.issueLabel === "string"
    && (
      value.language === undefined
      || value.language === "zh"
      || value.language === "en"
      || value.language === "bilingual"
    )
    && typeof value.overview === "string"
    && (value.sourceOverview === undefined || typeof value.sourceOverview === "string")
    && (value.translationModel === undefined || typeof value.translationModel === "string")
    && typeof value.model === "string"
    && typeof value.generatedAt === "string"
    && Array.isArray(value.articles)
    && value.articles.length > 0
    && value.articles.every(isArticle)
  );
}
